import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, RefreshCcw, Save, Info, Trophy, Plus } from "lucide-react";

/**
 * Admin · Growth · Competitor Intelligence
 * Public-data-only benchmark against the configured primary competitor.
 */

const PLATFORMS = ["LinkedIn", "Instagram", "Threads", "X", "YouTube", "TikTok", "Facebook"];
const NA = <span className="text-stone-400">—</span>;

function fmt(n) { return n == null ? NA : Number(n).toLocaleString(); }

export default function CompetitorIntel() {
    const [comps, setComps] = useState([]);
    const [primary, setPrimary] = useState("monarch");
    const [comparison, setComparison] = useState({ primary: {}, rows: [] });
    const [weekly, setWeekly] = useState(null);
    const [content, setContent] = useState({});
    const [snap, setSnap] = useState({ subject: "vault", platform: "LinkedIn", date: new Date().toISOString().slice(0, 10), follower_count: "", source: "Manual", confidence: "Verified" });

    const load = async () => {
        const [c, cmp, w, ct] = await Promise.all([
            api.get("/admin/competitor-intel/competitors").catch(() => null),
            api.get("/admin/competitor-intel/vault-vs-primary").catch(() => null),
            api.get("/admin/competitor-intel/brief/weekly").catch(() => null),
            api.get("/admin/competitor-intel/content/summary").catch(() => null),
        ]);
        setComps(c?.data?.competitors || []);
        setPrimary(c?.data?.primary || "monarch");
        setComparison(cmp?.data || { rows: [] });
        setWeekly(w?.data || null);
        setContent(ct?.data?.by_subject || {});
    };
    useEffect(() => { load(); }, []);

    const saveSnap = async () => {
        try {
            await api.post("/admin/competitor-intel/followers", {
                ...snap,
                follower_count: snap.follower_count === "" ? null : Number(snap.follower_count),
            });
            toast.success("Snapshot saved");
            setSnap(s => ({ ...s, follower_count: "" }));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    };

    const setAsPrimary = async (cid) => {
        try { await api.post("/admin/competitor-intel/competitors/set-primary", { competitor_id: cid }); toast.success("Primary updated"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't set primary"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="competitor-intel">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <TrendingUp size={12} /> Super Admin · Growth
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Competitor intelligence.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Public-data-only benchmark against <b>{comps.find(c => c.id === primary)?.name || "Monarch"}</b> and
                        secondary references. Nothing is scraped from private endpoints or fabricated.
                    </p>
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                        data-testid="comp-refresh">
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {/* Competitors */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6" data-testid="comp-list">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                    <Trophy size={13} className="text-stone-500" />
                    <h2 className="text-lg font-display text-stone-900">Competitors</h2>
                </div>
                {comps.map(c => (
                    <div key={c.id} className="border-b border-stone-100 last:border-0 px-4 py-3 flex items-center justify-between gap-2 flex-wrap"
                         data-testid={`comp-row-${c.id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${c.role === "primary" ? "bg-amber-100 text-amber-900" : "bg-stone-100 text-stone-700"}`}>
                                    {c.role === "primary" ? "PRIMARY" : "Secondary"}
                                </span>
                                <div className="text-[13.5px] font-semibold text-stone-900">{c.name}</div>
                                <span className="text-[11px] text-stone-500">{c.website}</span>
                            </div>
                            <div className="text-[11.5px] text-stone-500 mt-0.5">{c.reason}</div>
                        </div>
                        {c.role !== "primary" && (
                            <button onClick={() => setAsPrimary(c.id)}
                                    className="text-[11.5px] font-semibold text-stone-700 hover:text-stone-900 px-2.5 py-1 rounded border border-stone-200"
                                    data-testid={`comp-set-primary-${c.id}`}>
                                Set as primary
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* VAULT vs Primary comparison */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6" data-testid="comp-comparison">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
                    <h2 className="text-lg font-display text-stone-900">VAULT vs {comparison.primary?.name || "primary"}</h2>
                </div>
                <table className="w-full text-sm"><thead className="bg-stone-50 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold"><tr><th className="text-left px-4 py-2.5">Platform</th><th className="text-right px-4 py-2.5">VAULT</th><th className="text-right px-4 py-2.5">Growth %</th><th className="text-right px-4 py-2.5">{comparison.primary?.name || "Primary"}</th><th className="text-right px-4 py-2.5">Growth %</th><th className="px-4 py-2.5">Trend</th></tr></thead><tbody>{(comparison.rows || []).map(r => (
                        <tr key={r.platform} className="border-t border-stone-100" data-testid={`comp-row-plat-${r.platform}`}>
                            <td className="px-4 py-2.5 font-semibold text-stone-900">{r.platform}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{fmt(r.vault)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{r.vault_growth != null ? `${r.vault_growth}%` : NA}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{fmt(r.primary)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{r.primary_growth != null ? `${r.primary_growth}%` : NA}</td>
                            <td className="px-4 py-2.5">
                                <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${r.trend === "vault_faster" ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>
                                    {r.trend === "vault_faster" ? "VAULT faster" : "Primary faster"}
                                </span>
                            </td>
                        </tr>
                    ))}</tbody></table>
                <div className="px-4 py-2 bg-stone-50 border-t border-stone-100 text-[11px] text-stone-500 flex items-start gap-1.5">
                    <Info size={11} className="mt-0.5" /> {comparison.note}
                </div>
            </div>

            {/* Record snapshot */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-6" data-testid="comp-record-snapshot">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Record follower snapshot</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                    <select value={snap.subject} onChange={e => setSnap({...snap, subject: e.target.value})}
                            className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="snap-subject">
                        <option value="vault">VAULT</option>
                        {comps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={snap.platform} onChange={e => setSnap({...snap, platform: e.target.value})}
                            className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="snap-platform">
                        {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <input type="date" value={snap.date} onChange={e => setSnap({...snap, date: e.target.value})}
                           className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="snap-date" />
                    <input type="number" placeholder="Follower count" value={snap.follower_count}
                           onChange={e => setSnap({...snap, follower_count: e.target.value})}
                           className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="snap-count" />
                    <select value={snap.confidence} onChange={e => setSnap({...snap, confidence: e.target.value})}
                            className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="snap-confidence">
                        <option value="Verified">Verified</option>
                        <option value="Estimated">Estimated</option>
                        <option value="Awaiting">Awaiting Updated Data</option>
                    </select>
                    <button onClick={saveSnap}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                            data-testid="snap-save">
                        <Save size={11} /> Save
                    </button>
                </div>
                <div className="text-[11px] text-stone-500 mt-2">
                    Never estimate unavailable follower counts — leave blank and select <b>Awaiting Updated Data</b>.
                </div>
            </div>

            {/* Weekly brief */}
            {weekly && (
                <div className="bg-stone-900 text-white rounded-2xl p-5 mb-6" data-testid="comp-weekly-brief">
                    <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-amber-400 mb-1">
                        VAULT Weekly Competitor Brief
                    </div>
                    <div className="text-[11px] text-stone-400">{weekly.generated_at?.slice(0, 16).replace("T", " ")}</div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {PLATFORMS.slice(0, 4).map(p => {
                            const r = weekly.by_platform?.[p] || {};
                            return (
                                <div key={p} className="border border-white/10 rounded-lg p-3">
                                    <div className="text-[10.5px] uppercase tracking-widest text-stone-400 font-semibold">{p}</div>
                                    <div className="text-[12px] text-stone-100 mt-1">
                                        VAULT · {r.vault?.pct != null ? `${r.vault.pct}%` : "—"}
                                    </div>
                                    <div className="text-[12px] text-stone-100">
                                        {weekly.by_platform[p]?.primary_name || "Primary"} · {r.primary?.pct != null ? `${r.primary.pct}%` : "—"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 text-[11px] uppercase tracking-widest text-amber-400 font-semibold">Three actionable recommendations</div>
                    <ul className="mt-1 space-y-1 text-[13px] text-stone-100">
                        {(weekly.recommendations || []).map((r, i) => (
                            <li key={i} data-testid={`weekly-reco-${i}`}>· {r}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Content mix */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="comp-content-summary">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Content mix</div>
                {Object.keys(content).length === 0 ? (
                    <div className="text-center text-sm text-stone-400 py-6">
                        No content recorded yet. Add posts via the API to see theme mix + frequency.
                    </div>
                ) : Object.entries(content).map(([subj, data]) => (
                    <div key={subj} className="border-t border-stone-100 first:border-0 pt-3 mt-3 first:mt-0">
                        <div className="text-[13px] font-semibold text-stone-900">{subj}</div>
                        <div className="text-[11.5px] text-stone-500">
                            {data.total} posts · Most consistent: <b>{data.most_consistent_platform || "—"}</b> ·
                            Highest frequency: <b>{data.highest_frequency_platform || "—"}</b>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                            {Object.entries(data.theme_mix || {}).map(([t, pct]) => (
                                <span key={t} className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">
                                    {t} · {pct}%
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 max-w-3xl">
                <Info size={12} className="mt-0.5 shrink-0" />
                Data policy · Only approved APIs and administrator-entered public values. No scraping,
                no CAPTCHA bypass, no private data. Where a source is unavailable the metric is displayed
                as <b>Awaiting Updated Data</b>. Historical metrics before the baseline date are never fabricated.
            </div>
        </div>
    );
}
